import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';
import { 
  Ability,
  Character,
  CharacterAbility,
  CharacterSheetInterface,
  Condition
} from "@dndModels";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterActionInput from "@dndComponents/character/spells_and_abilities/CharacterActionInput";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import StarBox from "@components/input/buttons/StarBox";

interface Props {
  char_int?: CharacterSheetInterface;
  disabled: boolean;
  starred: boolean;
  immune: boolean;
  // active?: { level: number, source_true_id: string };
  condition: Condition;
  color?: string;
  onChange: (level: number) => void;
  onStar: () => void;
  onImmune: () => void;
  onCharChange?: () => void;
}

export default function ConditionBox({
  char_int,
  disabled,
  starred,
  immune,
  // active,
  condition,
  color = "lightgray",
  onChange,
  onStar,
  onImmune,
  onCharChange = () => {}
}: Props) {
  const [menu_open, setMenuOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [expanded_action_id, setExpandedActionId] = useState("");
  const [show_description, setShowDescription] = useState(true);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const renderCondition = () => {
    const cond = condition;
    const active = char_int instanceof Character ? char_int.conditions.find(o => o.source_id === cond._id) : undefined;
    const level = active ? active.level : 0;
    const has_cond = level > 0;
    const has_immunity = immune;
    return (
      <Grid container spacing={0} direction="row" 
        style={{ 
          cursor: "pointer"
        }}>
        <Grid
          style={{
            display: "flex"
          }}
          size={{
            xs: cond.immunity_exists || cond.max_level > 1 ? 5 : 12
          }}>
          <StarBox
            value={starred} 
            disabled={disabled}
            onChange={(value: boolean) => {
              onStar();
            }} 
          />
          { cond.max_level <= 1 && 
            <CheckBox
              value={has_cond} 
              disabled={has_immunity || disabled}
              onChange={(value: boolean) => {
                if (value) {
                  onChange(1);
                } else {
                  onChange(0);
                }
              }} 
            />
          }
          <span style={{ 
            cursor: "pointer", 
            color: (has_cond ? "green" : color), 
            fontWeight: (has_cond ? "bold" : "normal")  
          }} onClick={() => {
            setMenuOpen(true);
          }}>{ cond.name }</span>
        </Grid>
        { cond.immunity_exists && 
          <Grid
            style={{
              display: "flex"
            }}
            size={{
              xs: cond.max_level > 1 ? 4 : 7
            }}>
            <CheckBox
              value={has_immunity} 
              disabled={disabled}
              onChange={(value: boolean) => {
                onImmune();
              }} 
            />
            <span className="clickable" style={{ 
              color: (has_immunity ? "blue" : color), 
              fontWeight: (has_immunity ? "bold" : "normal")  
            }} onClick={() => {
              if (!disabled) {
                onImmune();
              }
            }}>Immune</span>
          </Grid>
        }
        { renderConditionLevels() }
      </Grid>
    );
  }

  const renderConditionLevels = () => {
    const cond = condition;
    const active = char_int instanceof Character ? char_int.conditions.find(o => o.source_id === cond._id) : undefined;
    const level = active ? active.level : 0;
    const has_immunity = immune;
    if (cond.max_level > 1) {
      const options: string[] = [];
      // I should change this to a SelectStringBox
      for (let i = 0; i <= cond.max_level; ++i) {
        options.push(`${i}`);
      }
      return (
        <Grid
          size={{
            xs: cond.immunity_exists ? 3 : 7
          }}>
          <SelectStringBox 
            name="Level"
            value={`${level}`}
            options={options}
            onChange={(value: string) => {
              if (!has_immunity && !disabled) {
                onChange(+value);
              }
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ menu_open } 
        onClose={() => {
          setMenuOpen(false);
        }}>
        { renderConditionDetails() }
      </ModalOrDrawer>
    );
  }

  const renderAbilities = () => {
    // debugger;
    if (char_int instanceof Character) {
      const filtered = char_int.actions.filter(o => 
        o.action instanceof CharacterAbility &&
        o.action.source_id === condition._id &&
        o.action.the_ability instanceof Ability &&
        o.action.the_ability.show_in.includes("Source"));
      if (filtered.length > 0) {
        return (
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              style={{
                fontSize: "14px",
              }}
              className={`header`}
              size={{
                xs: 12
              }}>
              &nbsp;Resting Abilities
            </Grid>
            { filtered.map((action, key: number) => {
              if (reloading) {
                return (<span key={key}></span>);
              } else {
                return (
                  <Grid
                    key={key}
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <CharacterActionInput
                        char={char_int}
                        char_interface={char_int}
                        action={action}
                        group={action.casting_time}
                        // show_extra_effects={true}
                        processing={false}
                        shared={false}
                        level={action.level}
                        // onCharChange={onCharChange}
                        // onItemChange={onChange}
                        // onTransform={onCharChange}
                        onOpen={() => {
                          setExpandedActionId(expanded_action_id === action.true_id ? "" : action.true_id);
                        }}
                      />
                    </Grid>
                    { expanded_action_id === action.true_id && 
                      <Grid
                        className="padded"
                        size={{
                          xs: 12
                        }}>
                        <DisplayStringWithReplacements display_string={ action.description } />
                      </Grid>
                    }
                  </Grid>
                );
              }
            })}
          </Grid>
        );
      }
    }
    return null;
  }

  const renderConditionDetails = () => {
    return (
      <div style={{ 
        height: "100%",
        overflowX: "hidden"
      }}>
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ fontWeight: "bold" }}
            size={{
              xs: 12
            }}>
            { condition.name }
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ToggleButtonBox
              name="Show Description"
              value={show_description}
              onToggle={() => {
                setShowDescription(!show_description);
              }}
            />
          </Grid>
          { show_description &&
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements display_string={ condition.description } />
            </Grid>
          }
          { renderAbilities() }
        </Grid>
      </div>
    );
  }

  return (
    <>
      { renderCondition() }
      { renderExtras() }
    </>
  );
}
